import { Box, Card, TextField, Typography } from "@mui/material"
import { ListOrdered } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { BROCHURE_COLORS, BROCHURE_FONT, bulletRows, escapeHtml, splitLines, type UseCaseSection } from "./model"

// Editor for the two-step solution flow — a heading + lead and exactly two
// steps, each with a title, a short role line, and one-per-line bullets. Two
// fixed steps keeps the flow the single, balanced focal element of the section.
interface StepsSectionProps {
  section: UseCaseSection
  onUpdate: (sectionId: string, partial: any) => void
}

function StepFields({ n, section, onUpdate }: { n: 1 | 2; section: UseCaseSection; onUpdate: (id: string, p: any) => void }) {
  const c = section.content
  const tk = `step${n}Title`, rk = `step${n}Role`, pk = `step${n}Points`
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>Step {n}</Typography>
      <TextField size="small" label="Title" InputLabelProps={{ shrink: true }}
        value={c[tk] || ""} onChange={(e) => onUpdate(section.id, { [tk]: e.target.value })}
        placeholder={n === 1 ? "e.g., PBX initiates the alert" : "e.g., USN takes over communication"} />
      <TextField size="small" label="Role line" InputLabelProps={{ shrink: true }}
        value={c[rk] || ""} onChange={(e) => onUpdate(section.id, { [rk]: e.target.value })}
        placeholder={n === 1 ? "e.g., Immediate · in-room" : "e.g., Mobile · everywhere next"} />
      <Typography component="label" sx={{ fontSize: 13, color: "text.secondary" }}>Bullets — one per line</Typography>
      <TextField multiline minRows={3} size="small"
        value={c[pk] || ""} onChange={(e) => onUpdate(section.id, { [pk]: e.target.value })}
        placeholder={n === 1 ? "Every room phone rings at once\nGuests get the first warning in seconds" : "Follow-up over SMS and mobile apps\nReal-time updates as the situation evolves"} />
    </Box>
  )
}

export function StepsSection({ section, onUpdate }: StepsSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <ListOrdered size={20} />
          The solution — a two-step flow
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-st-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Heading</Typography>
              <AiTextImprover fieldType="short" context="use-case brochure solution heading" currentText={c.heading} onImprove={(v) => onUpdate(section.id, { heading: v })} />
            </Box>
            <TextField fullWidth size="small" id={`uc-st-heading-${section.id}`}
              value={c.heading || ""} onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., A coordinated two-step response" />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-st-lead-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Intro</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator fieldType="long" context="use-case brochure solution intro — one sentence introducing the two-step approach"
                  placeholder="e.g., introduce the approach" currentValue={c.lead} onGenerate={(v) => onUpdate(section.id, { lead: v })} />
                <AiTextImprover fieldType="long" context="use-case brochure solution intro" currentText={c.lead} onImprove={(v) => onUpdate(section.id, { lead: v })} />
              </Box>
            </Box>
            <TextField fullWidth multiline minRows={2} size="small" id={`uc-st-lead-${section.id}`}
              value={c.lead || ""} onChange={(e) => onUpdate(section.id, { lead: e.target.value })}
              placeholder="One line introducing the two-step flow" />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <StepFields n={1} section={section} onUpdate={onUpdate} />
            <StepFields n={2} section={section} onUpdate={onUpdate} />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

function stepCard(n: number, title: string, role: string, points: string[]): string {
  return `
                    <td class="mobile-stack" width="50%" valign="top" style="width: 50%; vertical-align: top; padding: 8px;">
                      <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${BROCHURE_COLORS.soft}" style="background-color: ${BROCHURE_COLORS.soft}; border-radius: 12px;">
                        <tr>
                          <td style="padding: 20px 20px 12px 20px;">
                            <table cellpadding="0" cellspacing="0" border="0" role="presentation">
                              <tr>
                                <td valign="middle" style="vertical-align: middle; padding-right: 10px;">
                                  <table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>
                                    <td align="center" valign="middle" width="26" height="26" bgcolor="${BROCHURE_COLORS.navy}" style="width: 26px; height: 26px; background-color: ${BROCHURE_COLORS.navy}; border-radius: 13px; color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 12px; font-weight: bold; text-align: center; line-height: 26px;">${n}</td>
                                  </tr></table>
                                </td>
                                <td valign="middle" style="vertical-align: middle;">
                                  <p style="margin: 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 16px; font-weight: bold; line-height: 20px;">${escapeHtml(title)}</p>
                                  ${role ? `<p style="margin: 2px 0 0 0; color: ${BROCHURE_COLORS.lime}; font-family: ${BROCHURE_FONT}; font-size: 10px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase;">${escapeHtml(role)}</p>` : ''}
                                </td>
                              </tr>
                            </table>
                            <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-top: 12px;">${bulletRows(points)}
                            </table>
                          </td>
                        </tr>
                      </table>
                    </td>`
}

// Serialize the two-step flow: heading + lead, then two balanced step cards
// (numbered badge · title · role · dash bullets) — the section's single focal
// element, right-sized (not an oversized box). Stacks on mobile.
export function stepsToHtml(section: UseCaseSection): string {
  const c = section.content || {}
  const heading = c.heading ? escapeHtml(c.heading) : ""
  const lead = c.lead ? escapeHtml(c.lead) : ""
  const p1 = splitLines(c.step1Points)
  const p2 = splitLines(c.step2Points)
  const hasSteps = (c.step1Title || "").trim() || (c.step2Title || "").trim() || p1.length || p2.length
  if (!heading && !lead && !hasSteps) return ""

  return `
          <!-- Solution: two-step flow -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.card}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.card}; padding: 20px 32px 36px 32px;">
              ${heading ? `<h2 style="margin: 0 0 10px 0; padding: 0 8px; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 24px; line-height: 30px; font-weight: bold;">${heading}</h2>` : ''}
              ${lead ? `<p style="margin: 0 0 16px 0; padding: 0 8px; color: ${BROCHURE_COLORS.ink}; font-family: ${BROCHURE_FONT}; font-size: 16px; line-height: 25px;">${lead}</p>` : ''}
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  ${stepCard(1, c.step1Title || "", c.step1Role || "", p1)}
                  ${stepCard(2, c.step2Title || "", c.step2Role || "", p2)}
                </tr>
              </table>
            </td>
          </tr>
          `
}
